import { useStore } from '@nanostores/react';
import { animate } from 'motion';
import { AnimatePresence, motion as m } from 'motion/react';
import { atom } from 'nanostores';
import { useEffect, useRef } from 'react';

export type AlertProps = {
  title: string;
  message: string;
  confirmText: string;
  cancelText: string | null;
  bgDismiss?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export const $alerts = atom<AlertProps | null>(null);

function AlertDialogInternal({
  title,
  message,
  confirmText,
  cancelText,
  bgDismiss,
  onConfirm,
  onCancel,
}: AlertProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    // Focus cancel button if it exists, otherwise focus confirm
    const el = cancelRef.current ?? confirmRef.current;
    el?.focus();

    // Make the rest of the page inert (non-interactive) and hidden to assistive
    // tech while the dialog is open, and trap Tab navigation inside the dialog.
    const wrapper = containerRef.current?.parentElement ?? containerRef.current;
    const bodyChildren = Array.from(document.body.children);
    const previousStates = bodyChildren.map(node => ({
      node,
      inert: (node as any).inert,
      ariaHidden: node.getAttribute && node.getAttribute('aria-hidden'),
    }));

    bodyChildren.forEach(node => {
      if (node === wrapper || node.contains(wrapper)) return;
      try {
        (node as any).inert = true;
      } catch {}
      node.setAttribute('aria-hidden', 'true');
    });

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = containerRef.current?.querySelectorAll<HTMLElement>(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const nodes = Array.from(focusable);
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last!.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first!.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      // restore inert/aria-hidden states
      previousStates.forEach(({ node, inert, ariaHidden }) => {
        try {
          (node as any).inert = inert;
        } catch {}
        if (ariaHidden === null) node.removeAttribute('aria-hidden');
        else node.setAttribute('aria-hidden', ariaHidden as string);
      });
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  return (
    <m.div
      className="fixed top-0 z-99999 flex h-full w-full items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
    >
      <div
        className="fixed inset-0 bg-black opacity-50"
        onClick={
          bgDismiss
            ? onCancel
            : () => {
                animate(
                  containerRef.current!,
                  { scale: [1, 0.98, 1.02, 1] },
                  { type: 'tween', ease: 'easeInOut', duration: 0.4 },
                );
              }
        }
      />
      <div
        className="z-1 w-full max-w-md rounded bg-white p-4 shadow"
        ref={containerRef}
      >
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-2">{message}</p>
        <div className="mt-4 flex justify-end">
          {cancelText && (
            <button
              onClick={onCancel}
              ref={cancelRef}
              className="mr-2 rounded border border-gray-300 px-4 py-2"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={onConfirm}
            ref={confirmRef}
            className="rounded border border-blue-500 bg-blue-500 px-4 py-2 text-white"
          >
            {confirmText}
          </button>
        </div>
      </div>
    </m.div>
  );
}

export function AlertDialog() {
  const alert = useStore($alerts);

  return (
    <AnimatePresence>
      {alert && <AlertDialogInternal key={alert.title ?? 'alert'} {...alert} />}
    </AnimatePresence>
  );
}
